import { useState, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import { SubmitHandler } from 'react-hook-form'
import { AppConstantes } from '../../../utils/Constantes'
import { alertUsuarios } from '../../../utils/alerts/AlertUsuarios'
import { TTareaForm, style, useTareaForm } from '../models/tareaForm.type'
import {
  useGuardarTareaMutation,
  useActualizarTareaMutation,
  useRecuperarTareaporIdQuery,
  useLazyRecuperarCondicionantesPorIdAutorizacionQuery,
} from '../../../redux/api'
import dayjs from 'dayjs'
import toast, { Toaster } from 'react-hot-toast'
import { CargaInfo } from '../../../components/layout/CargaInfo'
import { ComentariosReportes } from './ComentariosReportes'
import { Tarea } from '../models'
import { Autorizacion, Condicionante } from '../../autorizaciones/models'
import { alertSuccess } from '../../../utils/alerts/AlertSuccess'
import { ModalTareaBody } from './ModalTareaBody'
import { ModalTareaHeader } from './ModalTareaHeader'
import { ModalTareaFooter } from './ModalTareaFooter'

type Props = {
  show: boolean
  isModoEditar: boolean
  idTarea: string
  close: () => void
  claveNivelTarea?: string
  autorizacion?: Autorizacion
  condicionante?: Condicionante
  fechaCalendario?: string
  refetchConsulta?: () => void
  setIsSuccessGuardar?: (arg: boolean) => void
  setIsSuccessActualizar?: (arg: boolean) => void
}

const ESTATUS_COMENTARIO = 'COMPLETADA'
const OPCIONES_BLOQUEADAS = 'BAJA'

export const ModalAgregarEditarTarea = ({
  idTarea,
  isModoEditar,
  close,
  show,
  claveNivelTarea,
  autorizacion,
  condicionante,
  fechaCalendario,
  refetchConsulta,
  setIsSuccessGuardar,
  setIsSuccessActualizar,
}: Props) => {
  const [condicionantes, setCondicionantes] = useState([])
  const [idSitio, setIdSitio] = useState<string>()
  const [mostrarComentario, setMostrarComentario] = useState(false)
  const [tareaFinalizada, setTareaFinalizada] = useState<Tarea>()
  const [tareaCargada, setTareaCargada] = useState(false)

  const [recuperarCondicionantes] = useLazyRecuperarCondicionantesPorIdAutorizacionQuery()

  const formTarea = useTareaForm()
  const {
    register,
    formState: { errors },
    control,
    reset,
    resetField,
    handleSubmit,
    getValues,
    setValue,
    watch,
    trigger,
  } = formTarea

  // Mutacion: alta de tarea.
  const [
    guardarTarea,
    {
      isLoading: isLoadingGuardar,
      isSuccess: isSuccessGuardar,
      isError: isErrorGuardar,
      error: errorGuardar,
    },
  ] = useGuardarTareaMutation()

  // Query: detalle de tarea por ID (solo en modo editar).
  const {
    data: tareaId,
    isSuccess,
    isError,
    error,
    isLoading,
    refetch,
  } = useRecuperarTareaporIdQuery(idTarea ?? '', {
    skip: !isModoEditar,
  })

  // Mutacion: actualizacion de tarea.
  const [
    actualizarTarea,
    {
      isSuccess: isSuccessActualizar,
      isLoading: isLoadingActualizar,
      isError: isErrorActualizar,
      error: errorActualizar,
    },
  ] = useActualizarTareaMutation()

  useEffect(() => {
    if (!isModoEditar && claveNivelTarea !== undefined && !isSuccessGuardar) {
      // Cuando la tarea se crea desde detalle de autorizacion/condicionante,
      // se precargan relaciones y se bloquean selects para mantener el contexto.
      setIdSitio(autorizacion?.idSitio)
      if (claveNivelTarea === AppConstantes.CLAVE_NIVEL_AUTORIZACION) {
        recuperarCondicionantesPorIdAutorizacion(autorizacion?.idAutorizacion)
        reset({
          idSitio: {
            valor: autorizacion?.idSitio,
            etiqueta: autorizacion?.nombreSitio,
          },
          idAutorizacion: {
            valor: autorizacion?.idAutorizacion,
            etiqueta: autorizacion?.descAutorizacion,
          },
        })
      }
      if (claveNivelTarea === AppConstantes.CLAVE_NIVEL_CONDICIONANTE) {
        reset({
          idSitio: {
            valor: autorizacion?.idSitio,
            etiqueta: autorizacion?.nombreSitio,
          },
          idAutorizacion: {
            valor: autorizacion?.idAutorizacion,
            etiqueta: autorizacion?.descAutorizacion,
          },
          idCondicionante: {
            valor: condicionante?.idCondicionante,
            etiqueta: condicionante?.nombre,
          },
        })
      }
    }
    if (!tareaCargada && isModoEditar && isSuccess) {
      // Hidrata formulario desde backend.
      // Normaliza:
      // - selects (estatus, sitio/autorizacion/condicionante, usuario) a `{valor, etiqueta}`
      // - fechas a formato compatible con datepicker
      // - recordatorios a `unidad` tipo Opcion
      reset({
        ...tareaId,
        idSitio: {
          valor: tareaId.idSitio,
          etiqueta: tareaId.nombreSitio,
        },
        estatus: {
          valor: tareaId.estatus,
          etiqueta: tareaId.descEstatus,
        },
        idAutorizacion: {
          valor: tareaId.idAutorizacion,
          etiqueta: tareaId.descAutorizacion,
        },
        idCondicionante: {
          valor: tareaId.idCondicionante,
          etiqueta: tareaId.nombreCondicionante,
        },
        idUsuarioAsignado: {
          valor: tareaId.idUsuarioAsignado,
          etiqueta: tareaId.nombreUsuarioAsignado ?? 'Sin usuario asignado',
        },
        fechaInicio: dayjs(`${tareaId.fechaInicio}`).format(
          AppConstantes.FORMATO_FECHA_FIREFOX_CHROME
        ),
        fechaTermino: dayjs(`${tareaId.fechaTermino}`).format(
          AppConstantes.FORMATO_FECHA_FIREFOX_CHROME
        ),
        recordatorios: tareaId.recordatorios?.map((r: any) => {
          return {
            ...r,
            cantidad: r.cantidad,
            unidad: {
              etiqueta: r.descUnidad,
              valor: r.unidad,
            },
          }
        }),
      })
      setTareaCargada(true)
      setIdSitio(tareaId?.idSitio)
    }

    if (isErrorGuardar || isErrorActualizar) {
      alertUsuarios(isModoEditar, handleSubmit, errorActualizar, errorGuardar)
    }
    if (isModoEditar) {
      // Reasegura sitio cuando se abre en modo editar (depende del payload de backend).
      setValue('idSitio', { valor: tareaId?.idSitio, etiqueta: tareaId?.nombreSitio })
      setIdSitio(tareaId?.idSitio)
    }
  }, [
    isErrorGuardar,
    isSuccessGuardar,
    isSuccessActualizar,
    isErrorActualizar,
    isError,
    isSuccess,
    isModoEditar,
  ])

  useEffect(() => {
    if (!isModoEditar && fechaCalendario) {
      // Preselecciona fecha de inicio al abrir desde calendario.
      reset({
        fechaInicio: dayjs(`${fechaCalendario}`).format(AppConstantes.FORMATO_FECHA_FIREFOX_CHROME),
      })
    }
  }, [])

  /**
   * @description recupera todas las condicionantes relacionadas a una autorización
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 21/02/2023
   * @param idAutorización de la autorización seleccionada
   */
  const recuperarCondicionantesPorIdAutorizacion = (idAutorizacion: any) => {
    recuperarCondicionantes(idAutorizacion)
      .unwrap()
      .then((resp: any) => {
        const condicionantes = resp.map((condicionante: any) => ({
          etiqueta: condicionante.nombre,
          valor: condicionante.idCondicionante,
        }))
        setCondicionantes(condicionantes)
      })
  }

  /**
   * @description Función que dispara toast y restablece estados al actualizar una tarea
   *
   * @author andres.martinez, Hábil MX
   * @version 1.0.0, 12/05/2023
   */
  const acutalizarTareaActions = () => {
    setTareaCargada(false)
    setIsSuccessActualizar && setIsSuccessActualizar(isSuccessActualizar)
    toast.success('Tarea actualizada', { style })
    refetchConsulta && refetchConsulta()
    refetch && refetch()
  }

  /**
   * @description Agrega una tarea nueva
   *
   * @author andres.martinez, Hábil MX
   * @version 1.0.0, 07/12/2022
   */
  const onSubmit: SubmitHandler<TTareaForm> = (data: TTareaForm) => {
    // Mapea valores de formulario (Opcion) al contrato plano esperado por backend.
    const tarea = {
      ...data,
      idSitio: data.idSitio.valor,
      idAutorizacion: data.idAutorizacion?.valor,
      idCondicionante: data.idCondicionante?.valor,
      fechaInicio: dayjs(data.fechaInicio).format(AppConstantes.FORMATO_FECHA_POST),
      fechaTermino: dayjs(data.fechaTermino).format(AppConstantes.FORMATO_FECHA_POST),
      estatus: data.estatus.valor,
      idUsuarioAsignado: data.idUsuarioAsignado?.valor,
      descEstatus: data.estatus.etiqueta,
      comentarioCierre: tareaId?.comentarioCierre,
    }

    if (!isModoEditar) {
      // Recordatorios: si la unidad no es personalizada, el backend calcula en base a `fechaReferencia`.
      // Nota: aqui se usa literal 'PERSONALIZADO' (deuda tecnica) en lugar de constante compartida.
      tarea.recordatorios = data.recordatorios?.map((r: any) => {
        return {
          ...r,
          cantidad: r?.cantidad,
          unidad: r.unidad?.valor,
          descUnidad: r.unidad?.etiqueta,
          fechaReferencia: r.unidad.valor !== 'PERSONALIZADO' ? tarea.fechaTermino : null,
          fechaRecordatorio: r.fechaRecordatorio
            ? dayjs(r.fechaRecordatorio).format(AppConstantes.FORMATO_FECHA_RECORDATORIO_POST)
            : null,
        }
      })
    }

    // Reglas de guardado:
    // - Si la tarea está en estatus bloqueado (BAJA), se evita persistir cambios y se cierra.
    // - Si al editar se selecciona COMPLETADA, se abre el paso adicional de comentario/evidencia.
    // - En otro caso: mutación guardar/actualizar estándar.
    if (tareaId?.estatus === OPCIONES_BLOQUEADAS) {
      close()
    } else if (isModoEditar && tarea.estatus === ESTATUS_COMENTARIO) {
      setTareaFinalizada(tarea)
      setMostrarComentario(true)
    } else if (isModoEditar) {
      actualizarTarea(tarea).then(() => {
        acutalizarTareaActions()
      })
    } else {
      guardarTarea(tarea)
        .unwrap()
        .then(() => {
          setIsSuccessGuardar && setIsSuccessGuardar(isSuccessGuardar)
          alertSuccess(
            '¡Tarea guardada!',
            'La tarea se encuentra registrada.',
            refetchConsulta,
            close
          )
        })
    }
  }

  return (
    <>
      <Toaster />
      <Modal
        show={show}
        size='xl'
        onHide={close}
        dialogClassName='modal-dialog mh-925px'
        backdrop='static'
        keyboard={false}
        scrollable={true}
        onExit={() => {
          setTareaCargada(false)
        }}>
        {isModoEditar && (isLoading || isError) ? (
          <>
            <div className='d-flex justify-content-end p-7'>
              <button
                onClick={close}
                className=' close btn btn-outline-dark btn-icon btn-circle btn-sm ms-5 d-flex col-12 '>
                <i className='fa-solid fa-xmark fs-1'></i>
              </button>
            </div>
            <CargaInfo
              mensaje={'Recuperando información de la tarea'}
              isError={isError}
              mensajeError={'Ocurrió un error al recuperar los datos de la tarea'}
              errorApi={error}
            />
          </>
        ) : (
          <>
            <ModalTareaHeader
              tarea={tareaId}
              isModoEditar={isModoEditar}
              close={close}></ModalTareaHeader>
            {!mostrarComentario ? (
              <>
                {(!isModoEditar || (isModoEditar && isSuccess)) && (
                  <>
                    <ModalTareaBody
                      tarea={tareaId}
                      condicionantes={condicionantes}
                      claveNivelTarea={claveNivelTarea}
                      isModoEditar={isModoEditar}
                      isLoadingGuardar={isLoadingGuardar}
                      isLoadingActualizar={isLoadingActualizar}
                      recuperarCondicionantesPorIdAutorizacion={
                        recuperarCondicionantesPorIdAutorizacion
                      }
                      setIdSitio={setIdSitio}
                      idSitio={idSitio}
                      onSubmit={onSubmit}
                      handleSubmit={handleSubmit}
                      refetch={refetch}
                      {...{
                        control,
                        register,
                        errors,
                        getValues,
                        watch,
                        resetField,
                        trigger,
                      }}></ModalTareaBody>

                    <ModalTareaFooter
                      isModoEditar={isModoEditar}
                      isLoadingGuardar={isLoadingGuardar}
                      isLoadingActualizar={isLoadingActualizar}
                      close={close}
                      {...{ watch }}></ModalTareaFooter>
                  </>
                )}
              </>
            ) : (
              <ComentariosReportes
                idTarea={idTarea}
                tarea={tareaFinalizada!}
                regresar={(mostrarComentario: boolean) => {
                  setMostrarComentario(mostrarComentario)
                }}
                refetch={refetch}
                refetchConsulta={refetchConsulta}
                setTareaFinalizada={setTareaFinalizada}
                estatus={tareaId?.estatus}
                cerrar={close}
              />
            )}
          </>
        )}
      </Modal>
    </>
  )
}
